/* Andersen Restorations - Find Your Part.   Source: theme-build/src/ymm/ar-ymm.js
 *
 * Two jobs, one file:
 *  1. Every [data-ar-ymm] finder: cascading Year / Make / Model dropdowns from the
 *     inline menu, remembering the shopper's vehicle between visits.
 *  2. The results page ([data-ar-ymm-results]): looks the vehicle up in
 *     assets/ar-ymm-index.json and renders Horizon's own product cards for the exact
 *     parts that fit, via sections/ar-ymm-cards.
 *
 * Why an index and not the collection filters: Search & Discovery checks make, model
 * and year independently, so a 1966 Falcon filter showed 30 parts where 9 fit. The
 * index holds every (product, make, model, year) row, so matching is exact.
 *
 * Measurement (2026-10-03):
 *  - GA4 event `part_finder_search` (vehicle, vehicle_year/make/model, parts_found,
 *    search_landing, finder_page). The submit only leaves a note in sessionStorage; the
 *    event fires on the page the shopper lands on, once the parts count is known, so it
 *    never races the navigation. Landing on a vehicle page from Google does not count.
 *  - Cart attributes `Vehicle` and `Vehicle chosen via`, so every order shows the car the
 *    shopper had selected (admin order page, "Additional details"). Written once per cart
 *    per vehicle, and cleared by "Not your car?". Buy-it-now / express buttons on the
 *    product page skip the cart, so those orders carry no vehicle.
 *
 * Vehicle page trail (2026-10-27): what the shopper did with the vehicle pages over the
 * last 30 days, kept in localStorage and copied onto the cart, so an order can be judged
 * "likely came from a vehicle page" without relying on GA4 or on Shopify's own journey
 * summary, which keeps only the first and last visit.
 *  - `Vehicle page entry`: visits that STARTED on a vehicle page, with where they came
 *    from (Google, Google Ads, Bing, a referring site, or direct) and the date.
 *  - `Vehicle pages viewed`: the vehicle pages seen, most recent first.
 *  - `Parts opened from vehicle page` / `Parts opened from finder results`: the products
 *    the shopper clicked through to from those lists. Matched against the order's line
 *    items, this ties the part bought to the page that showed it.
 */
(function () {
  'use strict';
  if (window.arYmm) return;
  var KEY = 'ar-ymm-vehicle';
  var PENDING = 'ar-ymm-pending';
  var CART_MARK = 'ar-ymm-cart';
  var TRAIL = 'ar-vp-trail';
  var TRAIL_DAYS = 30;                        // how far back a vehicle page still counts
  var GA4_ID = 'G-1NRQY6NT51';               // the GA4 stream layout/theme.liquid configures
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function readStored() {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function store(v) {
    try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ }
  }
  // Keep how the vehicle was first chosen while it stays the same vehicle.
  function remember(v, via, fallback) {
    var old = readStored();
    var same = old && vehicleKey(old) === vehicleKey(v);
    store({ year: v.year, make: v.make, model: v.model,
            via: via || (same && old.via) || fallback });
  }

  /* ---------------------------------------------------------------- measurement */
  function track(params, done) {
    var finish = function () { if (done) { var d = done; done = null; d(); } };
    if (typeof window.gtag !== 'function') { finish(); return; }
    var p = { send_to: GA4_ID };
    Object.keys(params).forEach(function (k) { p[k] = params[k]; });
    if (done) { p.event_callback = finish; p.event_timeout = 800; setTimeout(finish, 900); }
    window.gtag('event', 'part_finder_search', p);
  }
  function vehicleParams(v) {
    return { vehicle: [v.year, v.make, v.model].filter(Boolean).join(' '),
             vehicle_year: v.year ? String(v.year) : '', vehicle_make: v.make || '',
             vehicle_model: v.model || '' };
  }
  function setPending(v) {
    try {
      sessionStorage.setItem(PENDING, JSON.stringify({ key: vehicleKey(v), from: location.pathname, t: Date.now() }));
    } catch (e) {}
  }
  // The finder submit this page answers, if any (consumed on read).
  function takePending(v) {
    try {
      var p = JSON.parse(sessionStorage.getItem(PENDING) || 'null');
      sessionStorage.removeItem(PENDING);
      if (p && p.key === vehicleKey(v) && Date.now() - p.t < 120000) return p;
    } catch (e) {}
    return null;
  }
  function cartCookie() {
    var m = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    return m ? m[1] : '';
  }
  // Put the vehicle (null clears it) and the vehicle page trail on the cart. Skipped
  // when this cart already holds exactly these values.
  function syncCart(v) {
    var attrs = trailAttributes(readTrail());
    attrs['Vehicle'] = v ? vehicleName(v) : '';
    attrs['Vehicle chosen via'] = v ? (v.via || '') : '';
    var body = JSON.stringify({ attributes: attrs });
    try { if (sessionStorage.getItem(CART_MARK) === body + '|' + cartCookie()) return; } catch (e) {}
    fetch(ROOT.replace(/\/$/, '') + '/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body
    }).then(function (r) {
      // re-read the cookie: the update may have created the cart
      if (r.ok) try { sessionStorage.setItem(CART_MARK, body + '|' + cartCookie()); } catch (e) {}
    }).catch(function () {});
  }

  /* ---------------------------------------------------------------- vehicle page trail */
  function readTrail() {
    var t = null;
    try { t = JSON.parse(localStorage.getItem(TRAIL) || 'null'); } catch (e) {}
    t = t || {};
    var cutoff = Date.now() - TRAIL_DAYS * 864e5;
    ['land', 'seen', 'open'].forEach(function (k) {
      t[k] = (t[k] || []).filter(function (x) { return x && x.t > cutoff; });
    });
    return t;
  }
  function saveTrail(t) {
    try { localStorage.setItem(TRAIL, JSON.stringify(t)); } catch (e) {}
  }
  function hasTrail(t) { return t.land.length || t.seen.length || t.open.length; }
  // Newest first, one entry per key, capped.
  function pushUnique(list, item, key, cap) {
    return [item].concat(list.filter(function (x) { return key(x) !== key(item); })).slice(0, cap);
  }
  function navigationType() {
    try { return performance.getEntriesByType('navigation')[0].type; } catch (e) { return ''; }
  }
  // Where this visit came from, or null when the shopper moved here from another page of
  // the shop (or reloaded, or came Back), which is not a landing.
  function arrivalSource() {
    var nav = navigationType();
    if (nav === 'reload' || nav === 'back_forward') return null;
    var host = '';
    try { host = document.referrer ? new URL(document.referrer).hostname : ''; } catch (e) {}
    if (host && (host === location.hostname || /(^|\.)a-resto-parts\.com$/.test(host))) return null;
    var q = new URLSearchParams(location.search);
    if (q.get('gclid') || q.get('gbraid') || q.get('wbraid')) return 'Google Ads';
    if (q.get('msclkid')) return 'Microsoft Ads';
    if (q.get('utm_source')) return q.get('utm_source') + (q.get('utm_medium') ? ' / ' + q.get('utm_medium') : '');
    if (!host) return 'direct';
    if (/(^|\.)google\./.test(host)) return 'Google';
    if (/(^|\.)bing\.com$/.test(host)) return 'Bing';
    if (/(^|\.)(duckduckgo\.com|yahoo\.com|ecosia\.org)$/.test(host)) return host.split('.').slice(-2)[0];
    return host.replace(/^www\./, '');
  }
  function noteVehiclePage(name) {
    var t = readTrail(), now = Date.now(), src = arrivalSource();
    if (src) t.land = pushUnique(t.land, { n: name, s: src, t: now },
      function (x) { return x.n + '|' + x.s + '|' + new Date(x.t).toDateString(); }, 3);
    t.seen = pushUnique(t.seen, { n: name, t: now }, function (x) { return x.n; }, 5);
    saveTrail(t);
  }
  // Record the product a card on this list leads to, before the browser follows the link.
  function watchOpens(box, from, name) {
    function hit(e) {
      var a = e.target.closest && e.target.closest('a[href*="/products/"]');
      if (!a || !box.contains(a)) return;
      var m = a.pathname.match(/\/products\/([^\/?#]+)/);
      if (!m) return;
      var t = readTrail();
      t.open = pushUnique(t.open, { h: decodeURIComponent(m[1]), f: from, n: name, t: Date.now() },
        function (x) { return x.h + '|' + x.f; }, 10);
      saveTrail(t);
    }
    box.addEventListener('click', hit);
    box.addEventListener('auxclick', hit);    // middle-click opens a new tab
  }
  function ymd(ts) {
    var d = new Date(ts);
    return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
  }
  // The cart attributes, in a fixed "part; part" format the reports parse back.
  function trailAttributes(t) {
    function opened(from) {
      return t.open.filter(function (x) { return x.f === from; })
        .map(function (x) { return x.h + ' (' + x.n + ', ' + ymd(x.t) + ')'; }).join('; ');
    }
    return {
      'Vehicle page entry': t.land.map(function (x) {
        return x.n + ' page, from ' + x.s + ', ' + ymd(x.t);
      }).join('; '),
      'Vehicle pages viewed': t.seen.map(function (x) { return x.n; }).join('; '),
      'Parts opened from vehicle page': opened('vehicle page'),
      'Parts opened from finder results': opened('finder results')
    };
  }
  function parseJSON(node) {
    try { return JSON.parse((node && node.textContent) || 'null'); } catch (e) { return null; }
  }
  function slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
  function vehicleKey(v) { return v.year + '|' + v.make + '|' + v.model; }
  function vehicleName(v) { return v.year + ' ' + v.make + ' ' + v.model; }

  var indexPromise = null;
  function loadIndex(url) {
    if (!indexPromise) {
      indexPromise = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('index ' + r.status);
        return r.json();
      });
      indexPromise.catch(function () { indexPromise = null; });
    }
    return indexPromise;
  }

  /* ---------------------------------------------------------------- finder */
  function initFinder(root) {
    if (root.dataset.arReady) return;
    root.dataset.arReady = '1';
    var DATA = parseJSON(root.querySelector('[data-ymm-menu]')) || {};
    var PAGES = parseJSON(root.querySelector('[data-ymm-pages]')) || {};
    var form = root.querySelector('[data-ymm-form]');
    var sel = {
      year: root.querySelector('[data-ymm="year"]'),
      make: root.querySelector('[data-ymm="make"]'),
      model: root.querySelector('[data-ymm="model"]')
    };
    var go = root.querySelector('.ar-ymm__go');

    // years are run-length encoded: [[1964,1973],[1979]]
    function expand(runs) {
      var out = [];
      (runs || []).forEach(function (r) {
        for (var y = r[0]; y <= (r.length > 1 ? r[1] : r[0]); y++) out.push(y);
      });
      return out;
    }
    function hasYear(runs, y) {
      return (runs || []).some(function (r) { return y >= r[0] && y <= (r.length > 1 ? r[1] : r[0]); });
    }
    var makes = Object.keys(DATA).sort();
    var allYears = {};
    makes.forEach(function (mk) {
      Object.keys(DATA[mk]).forEach(function (md) {
        expand(DATA[mk][md]).forEach(function (y) { allYears[y] = 1; });
      });
    });
    var years = Object.keys(allYears).map(Number).sort(function (a, b) { return b - a; });

    function fill(node, values, placeholder, keep) {
      var prev = keep ? node.value : '';
      node.innerHTML = '';
      var o = document.createElement('option');
      o.value = ''; o.textContent = placeholder;
      node.appendChild(o);
      values.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v; opt.textContent = v;
        node.appendChild(opt);
      });
      if (prev && values.map(String).indexOf(String(prev)) === -1) prev = '';
      node.value = prev;
      node.disabled = values.length === 0;
    }
    function makesFor(year) {
      if (!year) return makes;
      return makes.filter(function (mk) {
        return Object.keys(DATA[mk]).some(function (md) { return hasYear(DATA[mk][md], year); });
      });
    }
    function modelsFor(make, year) {
      if (!make || !DATA[make]) return [];
      return Object.keys(DATA[make]).filter(function (md) {
        return !year || hasYear(DATA[make][md], year);
      }).sort();
    }
    function yearsFor(make, model) {
      if (!make || !DATA[make]) return years;
      var set = {};
      Object.keys(DATA[make]).forEach(function (md) {
        if (model && md !== model) return;
        expand(DATA[make][md]).forEach(function (y) { set[y] = 1; });
      });
      return Object.keys(set).map(Number).sort(function (a, b) { return b - a; });
    }
    function refresh(changed) {
      var y = sel.year.value ? Number(sel.year.value) : '';
      if (changed !== 'year') fill(sel.year, yearsFor(sel.make.value, sel.model.value), 'Choose Year', true);
      if (changed !== 'make') fill(sel.make, makesFor(y), 'Choose Make', true);
      fill(sel.model, modelsFor(sel.make.value, y), 'Choose Model', changed !== 'make');
      go.disabled = !sel.make.value;
    }
    function set(v) {
      if (!v || !DATA[v.make] || !DATA[v.make][v.model] || !hasYear(DATA[v.make][v.model], Number(v.year))) return false;
      fill(sel.make, makesFor(Number(v.year)), 'Choose Make');
      sel.make.value = v.make;
      fill(sel.model, modelsFor(v.make, Number(v.year)), 'Choose Model');
      sel.model.value = v.model;
      fill(sel.year, yearsFor(v.make, v.model), 'Choose Year');
      sel.year.value = String(v.year);
      go.disabled = false;
      return true;
    }

    fill(sel.year, years, 'Choose Year');
    fill(sel.make, makes, 'Choose Make');
    fill(sel.model, [], 'Choose Model');
    go.disabled = true;
    set(currentVehicle() || readStored());

    ['year', 'make', 'model'].forEach(function (k) {
      sel[k].addEventListener('change', function () {
        refresh(k);
        // warm the index while the shopper is still choosing
        if (sel.make.value) loadIndex(root.dataset.indexUrl).catch(function () {});
      });
    });

    function makeHandle(mk) { return 'make-' + slug(mk); }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var mk = sel.make.value, md = sel.model.value, yr = sel.year.value;
      if (!mk) return;
      if (md && yr) {
        var v = { year: Number(yr), make: mk, model: md };
        remember(v, 'Part finder');
        setPending(v);                     // the landing page sends the GA4 event
        var page = PAGES[vehicleKey(v)];   // inline copy of index.pg, no fetch needed
        window.location.href = page ||
          (root.dataset.resultsUrl + '?year=' + yr + '&make=' + encodeURIComponent(mk) +
           '&model=' + encodeURIComponent(md));
        return;
      }
      // Partial vehicle: the make collection. A model on its own is safe to filter
      // on; it is the model-year PAIRING the flat facets cannot keep. That page runs no
      // finder code, so the event goes now and the navigation waits for it (max ~0.9 s).
      var qs = md ? '?filter.p.m.custom.model=' + encodeURIComponent(md) : '';
      var dest = ROOT.replace(/\/$/, '') + '/collections/' + makeHandle(mk) + qs;
      var params = vehicleParams({ year: yr, make: mk, model: md });
      params.search_landing = 'make collection';
      params.finder_page = location.pathname;
      track(params, function () { window.location.href = dest; });
    });
  }

  function currentVehicle() {
    var p = new URLSearchParams(window.location.search);
    if (p.get('year') && p.get('make') && p.get('model')) {
      return { year: Number(p.get('year')), make: p.get('make'), model: p.get('model') };
    }
    var el = document.querySelector('[data-ar-vehicle], [data-ar-ymm-results][data-year]');
    if (el) return { year: Number(el.dataset.year), make: el.dataset.make, model: el.dataset.model };
    return null;
  }

  /* ---------------------------------------------------------------- results */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function lookup(index, v) {
    var byYear = ((index.v[v.make] || {})[v.model] || {})[String(v.year)] || [];
    return byYear.map(function (x) {
      var id = typeof x === 'number' ? x : x[0];
      var p = index.p[id];
      return {
        handle: p[0],
        types: p[1].map(function (t) { return index.t[t]; }),
        poly: !!(p[2] & 1),
        master: !!(p[2] & 4),
        note: typeof x === 'number' ? null : index.q[x[1]]
      };
    });
  }

  function fetchCards(handles) {
    var url = ROOT.replace(/\/$/, '') + '/search?type=product&section_id=ar-ymm-cards&q=' +
      encodeURIComponent(handles.join(' '));
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cards ' + r.status);
      return r.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var out = {};
      doc.querySelectorAll('.ar-ymm-card').forEach(function (c) { out[c.dataset.req] = c; });
      return out;
    });
  }

  function initResults(box) {
    if (box.dataset.arReady) return;
    box.dataset.arReady = '1';
    var v = currentVehicle() || readStored();
    var head = box.querySelector('[data-ymm-heading]');
    var status = box.querySelector('[data-ymm-status]');
    var chipsBox = box.querySelector('[data-ymm-chips]');
    var grid = box.querySelector('[data-ymm-grid]');
    if (!v) {
      status.textContent = 'Choose your vehicle above to see the parts that fit it.';
      return;
    }
    var search = takePending(v);
    remember(v, search && 'Part finder', 'Product page link');
    watchOpens(box, 'finder results', vehicleName(v));
    collapseBar(v);
    head.textContent = 'Parts that fit a ' + vehicleName(v);
    document.title = vehicleName(v) + ' Suspension & Steering Parts | ' + (box.dataset.shop || document.title);
    status.textContent = 'Finding parts…';

    loadIndex(box.dataset.indexUrl).then(function (index) {
      var items = lookup(index, v);
      if (search) {
        var params = vehicleParams(v);
        params.parts_found = items.length;
        params.search_landing = 'results page';
        params.finder_page = search.from;
        track(params);
      }
      if (!items.length) {
        status.innerHTML = '';
        status.appendChild(document.createTextNode('We have no parts listed for a ' + vehicleName(v) +
          ' yet. Search the catalogue, or contact us and we will check for you.'));
        return;
      }
      status.textContent = items.length + (items.length === 1 ? ' part fits' : ' parts fit') +
        ' this vehicle.';

      // placeholders keep best-seller order while the cards arrive in parallel
      var slots = items.map(function (it) {
        var s = el('div', 'ar-ymm-slot is-loading');
        s.dataset.handle = it.handle;
        grid.appendChild(s);
        return s;
      });
      var batches = [];
      for (var i = 0; i < items.length; i += 20) batches.push(items.slice(i, i + 20));
      return Promise.all(batches.map(function (b) {
        return fetchCards(b.map(function (it) { return it.handle; })).then(function (cards) {
          b.forEach(function (it) {
            var slot = slots[items.indexOf(it)];
            var card = cards[it.handle];
            slot.classList.remove('is-loading');
            if (!card) { slot.remove(); it.gone = true; return; }
            slot.appendChild(card);
            if (it.note) slot.appendChild(el('p', 'ar-ymm-note', 'Fits: ' + it.note));
          });
        });
      })).then(function () {
        var live = items.filter(function (it) { return !it.gone; });
        status.textContent = live.length + (live.length === 1 ? ' part fits' : ' parts fit') +
          ' a ' + vehicleName(v) + '.';
        buildChips(live, slots, chipsBox, status, v);
      });
    }).catch(function () {
      status.textContent = 'Sorry, the part finder could not load. Please refresh the page, or search by part number.';
    });
  }

  // On a phone the full finder fills the first screen before any part shows; once a
  // vehicle is on the page it folds to one line with a Change button.
  function collapseBar(v) {
    var bar = document.querySelector('.ar-ymm--bar');
    if (!bar || bar.querySelector('.ar-ymm__current')) return;
    var cur = el('div', 'ar-ymm__current');
    cur.appendChild(el('span', 'ar-ymm__current-name', vehicleName(v)));
    var btn = el('button', 'ar-ymm__change', 'Change');
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', function () {
      var open = bar.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.textContent = open ? 'Close' : 'Change';
    });
    cur.appendChild(btn);
    bar.querySelector('.ar-ymm__head').appendChild(cur);
    bar.classList.add('has-vehicle');
  }

  function buildChips(items, slots, box, status, v) {
    if (items.length < 4) return;
    var groups = [];
    var types = {};
    items.forEach(function (it) { it.types.forEach(function (t) { types[t] = (types[t] || 0) + 1; }); });
    var typeNames = Object.keys(types).sort(function (a, b) { return types[b] - types[a]; });
    if (typeNames.length > 1) groups.push({ key: 'type', label: 'Part type', options: typeNames, count: types });
    var poly = items.filter(function (it) { return it.poly; }).length;
    if (poly && poly < items.length) {
      groups.push({ key: 'mat', label: 'Bushings', options: ['Polyurethane', 'OEM spec'],
        count: { 'Polyurethane': poly, 'OEM spec': items.length - poly } });
    }
    if (!groups.length) return;
    var state = {};
    function match(it) {
      if (state.type && it.types.indexOf(state.type) === -1) return false;
      if (state.mat && (state.mat === 'Polyurethane') !== it.poly) return false;
      return true;
    }
    function apply() {
      var n = 0;
      items.forEach(function (it) {
        var s = slots.find(function (x) { return x.dataset.handle === it.handle; });
        var on = match(it);
        if (s) s.hidden = !on;
        if (on) n++;
      });
      status.textContent = (n === items.length ? '' : n + ' of ') + items.length +
        (items.length === 1 ? ' part fits' : ' parts fit') + ' a ' + vehicleName(v) + '.';
      box.querySelectorAll('button[data-k]').forEach(function (b) {
        var active = (state[b.dataset.k] || '') === b.dataset.v;
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
    groups.forEach(function (g) {
      var row = el('div', 'ar-ymm-chips__row');
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', g.label);
      row.appendChild(el('span', 'ar-ymm-chips__label', g.label));
      [''].concat(g.options).forEach(function (o) {
        var b = el('button', 'ar-ymm-chip', o ? o + ' (' + g.count[o] + ')' : 'All');
        b.type = 'button';
        b.dataset.k = g.key; b.dataset.v = o;
        b.addEventListener('click', function () { state[g.key] = o; apply(); });
        row.appendChild(b);
      });
      box.appendChild(row);
    });
    apply();
  }

  /* ---------------------------------------------------------------- product page */
  var TICK = '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CROSS = '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';

  function initFitCheck(box) {
    if (box.dataset.arReady) return;
    box.dataset.arReady = '1';
    var v = readStored();
    if (!v) return;
    loadIndex(box.dataset.indexUrl).then(function (index) {
      var handle = box.dataset.handle;
      var known = index.p.some(function (p) { return p[0] === handle; });
      if (!known) return;                       // accessories: no vehicle to check
      var items = lookup(index, v);
      var hit = items.filter(function (it) { return it.handle === handle; })[0];
      var name = vehicleName(v);
      box.innerHTML = '';
      var msg = el('span');
      if (hit) {
        box.className = 'ar-fit__check is-yes';
        box.insertAdjacentHTML('afterbegin', TICK);
        msg.appendChild(document.createTextNode('Fits your ' + name +
          (hit.note ? ' if it has: ' + hit.note : '') + '.'));
      } else {
        box.className = 'ar-fit__check is-no';
        box.insertAdjacentHTML('afterbegin', CROSS);
        msg.appendChild(document.createTextNode('Not listed for your ' + name + '. '));
        if (items.length) {
          var a = el('a', null, 'See the ' + items.length + ' parts that fit it');
          a.href = (index.pg && index.pg[vehicleKey(v)]) || (box.dataset.resultsUrl + '?year=' + v.year +
            '&make=' + encodeURIComponent(v.make) + '&model=' + encodeURIComponent(v.model));
          msg.appendChild(a);
        }
      }
      var clear = el('button', null, 'Not your car?');
      clear.type = 'button';
      clear.addEventListener('click', function () {
        try { localStorage.removeItem(KEY); } catch (e) {}
        syncCart(null);
        box.hidden = true;
      });
      msg.appendChild(clear);
      box.appendChild(msg);
      box.hidden = false;
    }).catch(function () {});
  }

  // A vehicle landing page: the parts are already server-rendered. Remember the vehicle
  // (so product pages can check fit) and add the configuration notes from the index.
  function initVehiclePage(box) {
    if (box.dataset.arReady) return;
    box.dataset.arReady = '1';
    var v = { year: Number(box.dataset.year), make: box.dataset.make, model: box.dataset.model };
    if (!v.year || !v.make || !v.model) return;
    var search = takePending(v);
    remember(v, search && 'Part finder', 'Vehicle page');
    noteVehiclePage(vehicleName(v));
    watchOpens(box, 'vehicle page', vehicleName(v));
    if (search) {
      var params = vehicleParams(v);
      params.parts_found = box.querySelectorAll('.ar-ymm-slot[data-handle]').length;
      params.search_landing = 'vehicle page';
      params.finder_page = search.from;
      track(params);
    }
    collapseBar(v);
    loadIndex(box.dataset.indexUrl).then(function (index) {
      var notes = {};
      lookup(index, v).forEach(function (it) { if (it.note) notes[it.handle] = it.note; });
      box.querySelectorAll('.ar-ymm-slot[data-handle]').forEach(function (s) {
        var n = notes[s.dataset.handle];
        if (n && !s.querySelector('.ar-ymm-note')) s.appendChild(el('p', 'ar-ymm-note', 'Fits: ' + n));
      });
    }).catch(function () {});
  }

  function boot() {
    document.querySelectorAll('[data-ar-vehicle]').forEach(initVehiclePage);
    document.querySelectorAll('[data-ar-ymm]').forEach(initFinder);
    document.querySelectorAll('[data-ar-ymm-results]').forEach(initResults);
    document.querySelectorAll('[data-ar-fitcheck]').forEach(initFitCheck);
    // after the inits, which may have just remembered a new vehicle or a page visit
    var v = readStored();
    if (v || hasTrail(readTrail())) syncCart(v);
  }
  window.arYmm = { boot: boot, loadIndex: loadIndex, lookup: lookup, stored: readStored };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // the theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', boot);
})();
